// WhatsApp share helpers
import { formatINR } from "@/lib/format";

const cleanPhone = (p) => (p || "").replace(/[^\d]/g, "");

export const waLink = (phone, text) => {
  const encoded = encodeURIComponent(text);
  const digits = cleanPhone(phone);
  if (digits) {
    // wa.me expects country code + number, no leading zeros/plus. Default to +91 if 10-digit.
    const full = digits.length === 10 ? `91${digits}` : digits;
    return `https://wa.me/${full}?text=${encoded}`;
  }
  return `https://wa.me/?text=${encoded}`;
};

export const buildWeekMessage = (week) => {
  const lines = [
    `📊 *Weekly Collection Report*`,
    `🗓️ ${week.label}`,
    ``,
    `*Totals*`,
    `• Cash Collected: ${formatINR(week.cash_collected)}`,
    `• Final Payable: ${formatINR(week.final_payable)}`,
    `• Paid: ${formatINR(week.paid_amount)}`,
    `• Balance: ${formatINR(week.balance)}`,
  ];
  if (week.min_guarantee_applied > 0) {
    lines.push(`• Min Guarantee Applied: ${formatINR(week.min_guarantee_applied)}`);
  }
  const bpm = week.by_payment_method || {};
  const hasPM = Object.values(bpm).some((v) => v > 0);
  if (hasPM) {
    lines.push("", "*Received By*");
    if (bpm.cash > 0) lines.push(`• Cash: ${formatINR(bpm.cash)}`);
    if (bpm.upi > 0) lines.push(`• UPI: ${formatINR(bpm.upi)}`);
    if (bpm.bank > 0) lines.push(`• Bank: ${formatINR(bpm.bank)}`);
    if (bpm.other > 0) lines.push(`• Other: ${formatINR(bpm.other)}`);
  }
  const rows = week.per_delivery_boy || [];
  if (rows.length) {
    lines.push("", "*Per Delivery Boy*");
    rows.forEach((r) => {
      lines.push(
        `• ${r.delivery_boy_name || "—"} — Cash ${formatINR(r.cash_collected)}, Paid ${formatINR(
          r.paid_amount
        )}, Balance ${formatINR(r.balance)}`
      );
    });
  }
  return lines.join("\n");
};

export const buildBoyWeekMessage = (week, boy) => {
  const lines = [
    `📊 *Weekly Statement*`,
    `👤 ${boy.delivery_boy_name || "—"}`,
    `🗓️ ${week.label}`,
    ``,
    `Entries: ${boy.entries}`,
    `Cash Collected: ${formatINR(boy.cash_collected)}`,
    `Earning: ${formatINR(boy.earning)}`,
    `Final Payable: ${formatINR(boy.final_payable)}`,
    `Paid: ${formatINR(boy.paid_amount)}`,
    `Balance: ${formatINR(boy.balance)}`,
  ];
  if (boy.min_guarantee_applied > 0) {
    lines.push(`Min Guarantee Applied: ${formatINR(boy.min_guarantee_applied)}`);
  }
  return lines.join("\n");
};

const prettyDate = (isoDate) => {
  try {
    const d = new Date(isoDate + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return isoDate;
  }
};

export const buildDailyEntryMessage = (entry) => {
  const pbm = entry.paid_by_method || {};
  const lines = [
    `📊 *Daily Statement*`,
    `🗓️ ${prettyDate(entry.entry_date)}`,
    `👤 ${entry.delivery_boy_name || "—"}`,
    ``,
    `*Payable Calculation*`,
    `Cash Collected: ${formatINR(entry.cash_collected)}`,
    `Earning: ${formatINR(entry.earning)}`,
    `Shop Deduction: ${formatINR(entry.shop_deduction)}`,
    `Other Deduction: ${formatINR(entry.other_deduction)}`,
    `➡️ *Final Payable: ${formatINR(entry.final_payable)}*`,
  ];
  if (entry.min_guarantee_applied > 0) {
    lines.push(`(Min Guarantee applied: ${formatINR(entry.min_guarantee_applied)})`);
  }
  lines.push("", "*Received*");
  const parts = [
    ["Cash", pbm.cash],
    ["UPI", pbm.upi],
    ["Bank", pbm.bank],
    ["Other", pbm.other],
  ].filter(([, v]) => (v || 0) > 0);
  if (parts.length === 0) {
    // legacy or nothing paid
    if ((entry.paid_amount || 0) > 0) {
      const method = (entry.payment_method || "cash").toUpperCase();
      lines.push(`• ${method}: ${formatINR(entry.paid_amount)}`);
    } else {
      lines.push(`• No payment received yet`);
    }
  } else {
    parts.forEach(([k, v]) => lines.push(`• ${k}: ${formatINR(v)}`));
  }
  lines.push(`*Total Paid: ${formatINR(entry.paid_amount)}*`);
  lines.push("", `*Balance: ${formatINR(entry.balance)}*`);
  if (entry.notes) {
    lines.push("", `📝 ${entry.notes}`);
  }
  return lines.join("\n");
};

export const buildDailySummaryMessage = (dashboardData) => {
  const t = dashboardData?.today || {};
  const pbm = t.by_payment_method || {};
  const lines = [
    `📊 *Daily Collection Summary*`,
    `🗓️ ${prettyDate(dashboardData?.date || "")}`,
    ``,
    `*Totals*`,
    `Cash Collected: ${formatINR(t.cash_collected)}`,
    `Final Payable: ${formatINR(t.final_payable)}`,
    `*Total Paid: ${formatINR(t.paid_amount)}*`,
    `Balance: ${formatINR(t.balance)}`,
  ];
  if (t.min_guarantee_applied > 0) {
    lines.push(`Min Guarantee: ${formatINR(t.min_guarantee_applied)}`);
  }
  const parts = [
    ["Cash", pbm.cash],
    ["UPI", pbm.upi],
    ["Bank", pbm.bank],
    ["Other", pbm.other],
  ].filter(([, v]) => (v || 0) > 0);
  if (parts.length) {
    lines.push("", "*Received By*");
    parts.forEach(([k, v]) => lines.push(`• ${k}: ${formatINR(v)}`));
  }
  const sf = dashboardData?.shortfalls_today || [];
  if (sf.length) {
    lines.push("", `⚠️ Min Guarantee Shortfalls: ${sf.length}`);
    sf.forEach((s) =>
      lines.push(`• ${s.delivery_boy_name || "—"} — ${formatINR(s.min_guarantee_applied)}`)
    );
  }
  lines.push("", `Entries today: ${t.count || 0}`);
  return lines.join("\n");
};
